import { Drive, DriveType } from "../../types/drive";
import { PowerPlant } from "../../types/powerPlants";
import { CalculationResult } from "../../types/calculations";
import { FuelRequirements, FuelTanks, FuelCalculation } from "../../types/fuel";

/**
 * Calculates reaction drive fuel consumption according to Traveller rules
 * @param thrust - The thrust rating of the reaction drive
 * @param hours - Number of hours of operation
 * @param shipTons - Total ship tonnage
 * @returns Fuel consumption in tons
 */
export const calculateReactionFuelConsumption = (
  thrust: number,
  hours: number,
  shipTons: number,
): number => {
  if (thrust === 0) {
    return 0.25 * hours; // 0.25 tons per hour for thrust 0
  }
  // 2.5% per thrust per hour of ship's total tonnage
  return shipTons * 0.025 * thrust * hours;
};

/**
 * Calculates jump drive fuel consumption according to Traveller rules
 * @param jumpRating - The jump rating of the drive
 * @param shipTons - Total ship tonnage
 * @returns Fuel consumption in tons
 */
export const calculateJumpFuelConsumption = (
  jumpRating: number,
  shipTons: number,
): number => {
  // 10% of total tonnage × maximum jump score
  return shipTons * 0.1 * jumpRating;
};

/**
 * Calculates power plant fuel consumption according to Traveller rules
 * @param powerPlant - The power plant configuration
 * @param weeks - Number of weeks of operation
 * @returns Fuel consumption in tons
 */
export const calculatePowerPlantFuelConsumption = (
  powerPlant: PowerPlant,
  weeks: number,
): number => {
  const powerPlantTons = powerPlant.weight || 0;

  if (powerPlant.type === "chemical") {
    // Chemical power plants: 10 tons per ton of power plant for every 2 weeks
    // Allow fractional periods for proportional fuel consumption
    const twoWeekPeriods = weeks / 2;
    return powerPlantTons * 10 * twoWeekPeriods;
  } else {
    // Other power plants: 10% of power plant size per month (minimum 1 ton)
    const monthlyRate = Math.max(1, Math.ceil(powerPlantTons * 0.1));
    // Allow fractional months for proportional fuel consumption
    const months = weeks / 4; // Convert weeks to months (4 weeks = 1 month)
    return monthlyRate * months;
  }
};

/**
 * Calculates total fuel requirements for all systems
 * @param drives - Array of drives
 * @param powerPlant - Power plant configuration
 * @param shipTons - Total ship tonnage
 * @param weeks - Number of weeks of operation
 * @returns Fuel requirements breakdown
 */
export const calculateFuelRequirements = (
  drives: Drive[],
  powerPlant: PowerPlant | null,
  shipTons: number,
  weeks: number,
): FuelRequirements => {
  let reactionDrive = 0;
  let jumpDrive = 0;
  let powerPlantFuel = 0;

  // Calculate reaction drive fuel (only use primary drive for fuel calculations)
  const primaryReactionDrive = drives.find((d) => d.type === DriveType.REACTION && d.role === 'primary');
  if (primaryReactionDrive) {
    const hoursPerWeek = 168; // 7 days × 24 hours
    const totalHours = weeks * hoursPerWeek;
    reactionDrive += calculateReactionFuelConsumption(
      primaryReactionDrive.rating,
      totalHours,
      shipTons,
    );
  }

  // Calculate jump drive fuel (only use primary drive for fuel calculations)
  const primaryJumpDrive = drives.find((d) => d.type === DriveType.JUMP && d.role === 'primary');
  if (primaryJumpDrive) {
    jumpDrive += calculateJumpFuelConsumption(primaryJumpDrive.rating, shipTons);
  }

  // Calculate power plant fuel
  if (powerPlant) {
    powerPlantFuel = calculatePowerPlantFuelConsumption(powerPlant, weeks);
  }

  const total = reactionDrive + jumpDrive + powerPlantFuel;

  return {
    reactionDrive,
    jumpDrive,
    powerPlant: powerPlantFuel,
    total,
  };
};

/**
 * Calculates fuel tank allocation based on requirements
 * @param requirements - Fuel requirements
 * @param duration - Duration in weeks
 * @returns Fuel tank configuration
 */
export const calculateFuelTanks = (
  requirements: FuelRequirements,
  duration: number,
): FuelTanks => {
  return {
    reactionDrive: requirements.reactionDrive,
    jumpDrive: requirements.jumpDrive,
    powerPlant: requirements.powerPlant,
    total: requirements.total,
    duration,
  };
};

/**
 * Calculates fuel tank requirements based on power plant and drives
 * @param params - Parameters for fuel tank calculation
 * @returns Calculation result containing fuel tank configuration or an error
 */
export const calculateFuelTankRequirements = (params: {
  powerPlant: PowerPlant;
  drives: Drive[];
  shipSize: number;
  duration?: number; // Duration in weeks, defaults to 4 weeks
}): CalculationResult<FuelTanks> => {
  const { powerPlant, drives, shipSize, duration = 4 } = params;

  try {
    // Calculate fuel requirements
    const fuelRequirements = calculateFuelRequirements(drives, powerPlant, shipSize, duration);

    // Calculate fuel tanks
    const fuelTanks = calculateFuelTanks(fuelRequirements, duration);

    return {
      success: true,
      data: fuelTanks,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate fuel tank requirements: ${error instanceof Error ? error.message : 'Unknown error'}`,
      },
    };
  }
};

/**
 * Calculates total thrust for combat calculations
 * @param thrust - Thrust rating
 * @param hours - Hours of operation
 * @returns Total thrust for combat
 */
export const calculateTotalThrust = (thrust: number, hours: number): number => {
  // Total Thrust = Thrust × hours × 10 (10 combat rounds per hour)
  return thrust * hours * 10;
};

/**
 * Comprehensive fuel calculation including all systems
 * @param drives - Array of drives
 * @param powerPlant - Power plant configuration
 * @param shipTons - Total ship tonnage
 * @param weeks - Number of weeks of operation
 * @returns Complete fuel calculation
 */
export const calculateFuelCalculation = (
  drives: Drive[],
  powerPlant: PowerPlant | null,
  shipTons: number,
  weeks: number,
): FuelCalculation => {
  const requirements = calculateFuelRequirements(
    drives,
    powerPlant,
    shipTons,
    weeks,
  );
  const tanks = calculateFuelTanks(requirements, weeks);

  // Calculate total thrust for combat (using highest reaction drive)
  const reactionDrives = drives.filter((d) => d.type === DriveType.REACTION);
  const maxThrust =
    reactionDrives.length > 0
      ? Math.max(...reactionDrives.map((d) => d.rating))
      : 0;
  const totalThrust = calculateTotalThrust(maxThrust, weeks * 168);

  return {
    requirements,
    tanks,
    totalThrust,
  };
};

/**
 * Validates fuel tank allocation
 * @param tanks - Fuel tank configuration
 * @param requirements - Fuel requirements
 * @returns Array of validation errors
 */
export const validateFuelTanks = (
  tanks: FuelTanks,
  requirements: FuelRequirements,
): string[] => {
  const errors: string[] = [];

  if (tanks.total < requirements.total) {
    errors.push(
      `Insufficient fuel tanks. Required: ${requirements.total.toFixed(1)} tons, Allocated: ${tanks.total.toFixed(1)} tons`,
    );
  }

  if (tanks.reactionDrive < requirements.reactionDrive) {
    errors.push(
      `Insufficient reaction drive fuel. Required: ${requirements.reactionDrive.toFixed(1)} tons, Allocated: ${tanks.reactionDrive.toFixed(1)} tons`,
    );
  }

  if (tanks.jumpDrive < requirements.jumpDrive) {
    errors.push(
      `Insufficient jump drive fuel. Required: ${requirements.jumpDrive.toFixed(1)} tons, Allocated: ${tanks.jumpDrive.toFixed(1)} tons`,
    );
  }

  if (tanks.powerPlant < requirements.powerPlant) {
    errors.push(
      `Insufficient power plant fuel. Required: ${requirements.powerPlant.toFixed(1)} tons, Allocated: ${tanks.powerPlant.toFixed(1)} tons`,
    );
  }

  return errors;
};

/**
 * Calculates the cost of fuel based on fuel selection
 * @param fuelSelection - The fuel selection configuration
 * @returns The total cost of the fuel
 */
export const calculateFuelCost = (fuelSelection: any): number => {
  if (!fuelSelection) return 0;

  // If fuelSelection has a cost property, use it
  if (typeof fuelSelection.cost === "number") {
    return fuelSelection.cost;
  }

  // If fuelSelection has tons and costPerTon properties, calculate cost
  if (
    typeof fuelSelection.tons === "number" &&
    typeof fuelSelection.costPerTon === "number"
  ) {
    return fuelSelection.tons * fuelSelection.costPerTon;
  }

  // Default to 0 if no cost information is available
  return 0;
};

/**
 * Calculates the total usable fuel tankage, including optional systems and reductions.
 * @param fuelTanks - Main fuel tanks (tons)
 * @param optionalFuelSystems - Array of optional fuel systems (from Redux)
 * @returns Usable fuel tankage in tons
 */
export function calculateTotalUsableFuel(
  fuelTanks: number,
  optionalFuelSystems: any[],
): number {
  // Add capacity from Collapsible Fuel Tanks, Drop Tanks, Fuel/Cargo Containers set to Fuel, Mountable Tanks
  const collapsibleTanksCapacity = optionalFuelSystems
    .filter((s) => s.subtype === "collapsibleTank")
    .reduce((sum, s) => sum + (s.properties?.fullTonnage || 0), 0);
  const dropTanksCapacity = optionalFuelSystems
    .filter((s) => s.subtype === "dropTank")
    .reduce((sum, s) => sum + (s.properties?.dropTankTonnage || 0), 0);
  const fuelCargoContainerCapacity = optionalFuelSystems
    .filter(
      (s) =>
        s.subtype === "fuelCargoContainer" &&
        s.properties?.allocation === "fuel",
    )
    .reduce((sum, s) => sum + (s.properties?.capacity || 0), 0);
  const mountableTankCapacity = optionalFuelSystems
    .filter((s) => s.subtype === "mountableTank")
    .reduce((sum, s) => sum + (s.tonnage || 0), 0);

  // Find all installed Fuel Tank Compartments
  const fuelTankCompartments = optionalFuelSystems.filter(
    (s) => s.subtype === "fuelTankCompartment",
  );
  // Calculate total reduction based on compartment tonnage
  const totalCompartmentReduction = fuelTankCompartments.reduce(
    (sum, comp) => {
      // Each compartment reduces fuel tankage by its tonnage
      return sum + (comp.tonnage || 0);
    },
    0,
  );

  // Check for Metal Hydride Storage
  const hasMetalHydride = optionalFuelSystems.some(
    (s) => s.subtype === "metalHydride",
  );

  // Total available tankage is main fuelTanks + optional tanks
  let totalPhysicalTankage =
    fuelTanks +
    collapsibleTanksCapacity +
    dropTanksCapacity +
    fuelCargoContainerCapacity +
    mountableTankCapacity;

  // Subtract compartment reductions first
  let totalAfterReductions = totalPhysicalTankage - totalCompartmentReduction;

  // Then halve if Metal Hydride Storage is installed
  if (hasMetalHydride) {
    totalAfterReductions = totalAfterReductions / 2;
  }

  // Debug logging
  console.log("[calculateTotalUsableFuel] fuelTanks:", fuelTanks);
  console.log(
    "[calculateTotalUsableFuel] collapsibleTanksCapacity:",
    collapsibleTanksCapacity,
  );
  console.log(
    "[calculateTotalUsableFuel] dropTanksCapacity:",
    dropTanksCapacity,
  );
  console.log(
    "[calculateTotalUsableFuel] fuelCargoContainerCapacity:",
    fuelCargoContainerCapacity,
  );
  console.log(
    "[calculateTotalUsableFuel] mountableTankCapacity:",
    mountableTankCapacity,
  );
  console.log(
    "[calculateTotalUsableFuel] totalPhysicalTankage:",
    totalPhysicalTankage,
  );
  console.log(
    "[calculateTotalUsableFuel] totalCompartmentReduction:",
    totalCompartmentReduction,
  );
  console.log("[calculateTotalUsableFuel] hasMetalHydride:", hasMetalHydride);
  console.log(
    "[calculateTotalUsableFuel] totalAfterReductions:",
    totalAfterReductions,
  );

  return Math.max(0, totalAfterReductions);
}

/**
 * Validates that the ship has enough usable fuel (main + optional systems) for requirements.
 * @param requiredFuel - The required fuel in tons
 * @param fuelTanks - Main fuel tanks (tons)
 * @param optionalFuelSystems - Array of optional fuel systems (from Redux)
 * @returns Array of validation errors (empty if sufficient)
 */
export function validateUsableFuel(
  requiredFuel: number,
  fuelTanks: number,
  optionalFuelSystems: any[],
): string[] {
  const usableFuel = calculateTotalUsableFuel(fuelTanks, optionalFuelSystems);
  if (usableFuel < requiredFuel) {
    return [
      `Insufficient usable fuel. Required: ${requiredFuel.toFixed(1)} tons. Usable after compartments: ${usableFuel.toFixed(1)} tons.`,
    ];
  }
  return [];
}
